import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, Calendar, Loader, Target, ChevronRight, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

// ─── Animations ──────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`100%{transform:rotate(360deg)}`;

// ─── Styled Components ────────────────────────────────────
const Page = styled.div`
  display:flex;flex-direction:column;height:calc(100vh - 64px);
  background:#0a0a12;color:#fff;font-family:'Inter',sans-serif;
`;
const PageHeader = styled.div`
  padding:16px 20px;border-bottom:1px solid #1a1a2e;background:#0f0f1a;
  display:flex;align-items:center;justify-content:space-between;
`;
const BackBtn = styled(Link)`
  color:#888;display:flex;align-items:center;text-decoration:none;font-size:14px;
  &:hover{color:#a78bfa}
`;
const Title = styled.h2`
  font-size:18px;margin:0;display:flex;align-items:center;gap:8px;
  .badge{font-size:10px;background:linear-gradient(135deg,#7c3aed,#4338ca);padding:2px 8px;border-radius:10px;font-weight:700;}
`;
const Layout = styled.div`
  display:flex;flex:1;overflow:hidden;
  @media(max-width:768px){flex-direction:column;overflow-y:auto}
`;
const Sidebar = styled.div`
  width:300px;border-right:1px solid #1a1a2e;display:flex;flex-direction:column;
  background:#0d0d18;
  @media(max-width:768px) {
    width:100%; 
    border-right:none;
    display: ${p => p.$hiddenOnMobile ? 'none' : 'flex'};
  }
`;
const ListHeader = styled.div`
  padding:12px 16px;font-size:12px;color:#4b5563;border-bottom:1px solid #1a1a2e;
  text-transform:uppercase;letter-spacing:.5px;
`;
const SnapshotList = styled.div`flex:1;overflow-y:auto`;
const SnapshotItem = styled.div`
  padding:14px 16px;border-bottom:1px solid #12121f;cursor:pointer;
  background:${p=>p.$active?'rgba(124,58,237,.12)':'transparent'};
  border-left:3px solid ${p=>p.$active?'#7c3aed':'transparent'};
  &:hover{background:${p=>p.$active?'rgba(124,58,237,.12)':'rgba(124,58,237,.04)'}}
`;
const SnapTitle = styled.div`
  font-weight:600;font-size:13px;margin-bottom:3px;
  color:${p=>p.$active?'#a78bfa':'#ccc'};
`;
const SnapMeta = styled.div`font-size:11px;color:#4b5563;display:flex;gap:10px`;
const Detail = styled.div`
  flex:1;overflow-y:auto;padding:24px;
  @media(max-width:768px) {
    padding:16px;
    display: ${p => p.$visibleOnMobile ? 'block' : 'none'};
  }
`;
const DetailTitle = styled.div`
  font-size:22px;font-weight:700;color:#a78bfa;margin-bottom:4px;
`;
const SectionTitle = styled.h3`
  font-size:13px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.7px;
  margin:24px 0 14px;display:flex;align-items:center;gap:8px;
`;
const PivotCard = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:14px 16px;border-radius:10px;margin-bottom:8px;
  background:rgba(124,58,237,.04);border:1px solid rgba(124,58,237,.1);
  cursor:pointer;transition:all .18s;animation:${fadeIn} .3s;
  &:hover{background:rgba(124,58,237,.1);border-color:rgba(124,58,237,.3)}
`;
const StatusBadge = styled.span`
  font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;margin-left:6px;
  background:${p=>p.s==='BREAKOUT'?'rgba(239,68,68,.15)':p.s==='NEAR_BREAKOUT'?'rgba(234,179,8,.12)':'rgba(99,102,241,.1)'};
  color:${p=>p.s==='BREAKOUT'?'#ef4444':p.s==='NEAR_BREAKOUT'?'#eab308':'#818cf8'};
`;
const PerformanceBadge = styled.div`
  display:flex;flex-direction:column;align-items:flex-end;
  background:${p=>p.$neutral?'rgba(255,255,255,.03)':'rgba(239,68,68,.1)'};
  padding:4px 8px;border-radius:6px;
  border:1px solid ${p=>p.$neutral?'#1a1a2e':'rgba(239,68,68,.2)'};
`;
const EmptyMsg = styled.div`
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  height:100%;color:#374151;gap:12px;font-size:14px;padding:40px;text-align:center;
  @media(max-width:768px) {
    display: ${p => p.$hideOnMobile ? 'none' : 'flex'};
  }
`;
const Spinner = styled(Loader)`animation:${spin} 1s linear infinite;margin-bottom:16px;`;
const SyncingOverlay = styled.div`
  position:fixed;top:0;left:0;width:100%;height:100%;
  background:rgba(0,0,0,0.85);display:flex;flex-direction:column;
  align-items:center;justify-content:center;z-index:9999;backdrop-filter:blur(4px);
`;

export default function LivermoreV20HistoryPage() {
    const [snapshots, setSnapshots] = useState([]);
    const [selectedSnap, setSelectedSnap] = useState(null);
    const [details, setDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        apiService.getLivermoreV20History().then(data => {
            setSnapshots(data || []);
            // 모바일이 아닐 경우 기본 첫 항목 로드
            if (data && data.length > 0 && window.innerWidth > 768) {
                handleSelect(data[0]);
            }
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    const handleSelect = async (snap) => {
        setSelectedSnap(snap);
        setDetailsLoading(true);
        setDetails(null);
        try {
            const data = await apiService.getLivermoreV20HistoryDetail(snap.id);
            setDetails(data);

            // JIT 성능 계산 - 전체 pivot 중 하나라도 미계산이면 실행
            const needsCalc = data.pivotPoints?.some(
                p => !p.performance || p.performance.todayMax === '-' || p.performance.nextDayMax === '-'
            );
            if (data.pivotPoints?.length > 0 && needsCalc) {
                const createdAt = new Date(data.createdAt);
                const nextDay = new Date(createdAt);
                nextDay.setDate(nextDay.getDate() + 1);
                nextDay.setHours(15, 30, 0, 0);
                if (new Date() > nextDay) {
                    const updated = await apiService.calculateLivermoreV20Performance(snap.id);
                    if (updated.success) {
                        setDetails(prev => ({ ...prev, pivotPoints: updated.pivotPoints }));
                    }
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleRecalculate = async () => {
        if (!selectedSnap) return;
        const confirmed = window.confirm('현재 성과를 다시 계산하시겠습니까?\n(분봉 데이터 부족 시 KIS에서 다시 조회합니다)');
        if (!confirmed) return;

        setIsSyncing(true);
        setSyncMessage('성과 재계산 중...');
        await new Promise(resolve => setTimeout(resolve, 50)); // React가 오버레이를 먼저 렌더링하도록 yield
        try {
            const updated = await apiService.calculateLivermoreV20Performance(selectedSnap.id, true);
            if (updated.success) {
                setDetails(prev => ({ ...prev, pivotPoints: updated.pivotPoints }));
                alert('성과 재계산이 완료되었습니다.');
            }
        } catch (e) {
            console.error(e);
            alert('재계산 중 오류가 발생했습니다.');
        } finally {
            setIsSyncing(false);
        }
    };

    const formatTime = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleString('ko-KR', {
            month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false
        });
    };

    return (
        <Page>
            {isSyncing && (
                <SyncingOverlay>
                    <Spinner size={48} color="#7c3aed" />
                    <h2 style={{ margin: 0, marginBottom: '8px', color: '#a78bfa' }}>{syncMessage || '분석 중...'}</h2>
                    <p style={{ margin: 0, color: '#aaa', fontSize: '14px' }}>
                        KIS 분봉 데이터를 조회하여 성과를 계산합니다...
                    </p>
                </SyncingOverlay>
            )}
            <PageHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {isMobile && selectedSnap ? (
                        <button 
                            onClick={() => setSelectedSnap(null)}
                            style={{ background: 'none', border: 'none', color: '#a78bfa', display: 'flex', alignItems: 'center', padding: 0 }}
                        >
                            <ChevronLeft size={20} /> 리스트
                        </button>
                    ) : (
                        <BackBtn to="/labs/livermore-v20"><ChevronLeft size={16} />뒤로</BackBtn>
                    )}
                </div>
                <Title>
                    <Calendar size={18} color="#7c3aed"/>
                    Livermore V2.0 히스토리
                    <span className="badge">V2.0</span>
                </Title>
                <button 
                    onClick={handleRecalculate} 
                    disabled={isSyncing || !selectedSnap}
                    style={{ 
                        background: selectedSnap ? 'rgba(124, 58, 237, 0.2)' : '#1a1a2e', 
                        color: selectedSnap ? '#a78bfa' : '#4b5563', 
                        border: `1px solid ${selectedSnap ? '#7c3aed' : '#1a1a2e'}`,
                        padding: '6px 14px', borderRadius: '8px', fontSize: '12px', 
                        fontWeight: 700, cursor: (isSyncing || !selectedSnap) ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    {isSyncing ? <Spinner size={12} style={{ margin: 0 }} /> : <Zap size={12} />}
                    {isSyncing ? '계산 중...' : '다시 분석하기'}
                </button>
            </PageHeader>

            <Layout>
                {/* 사이드바 */}
                <Sidebar $hiddenOnMobile={isMobile && selectedSnap}>
                    <ListHeader>RECORDED SNAPSHOTS</ListHeader>
                    <SnapshotList>
                        {loading && <EmptyMsg><Loader size={20} color="#7c3aed"/>로딩 중...</EmptyMsg>}
                        {!loading && snapshots.length === 0 && (
                            <EmptyMsg>저장된 기록이 없습니다</EmptyMsg>
                        )}
                        {snapshots.map(snap => (
                            <SnapshotItem
                                key={snap.id}
                                $active={selectedSnap?.id === snap.id}
                                onClick={() => handleSelect(snap)}
                            >
                                <SnapTitle $active={selectedSnap?.id === snap.id}>
                                    {formatTime(snap.createdAt)} Analysis
                                </SnapTitle>
                                <SnapMeta>
                                    <span style={{color:snap.marketMood==='HOT'?'#ef4444':'#6b7280'}}>
                                        {snap.marketMood}
                                    </span>
                                    <span>{snap.leaderCount}L | {snap.pivotCount}P</span>
                                </SnapMeta>
                            </SnapshotItem>
                        ))}
                    </SnapshotList>
                </Sidebar>

                {/* 상세 */}
                {!selectedSnap ? (
                    <EmptyMsg style={{flex:1}} $hideOnMobile={isMobile}>
                        <Calendar size={40} color="#1a1a2e"/>
                        <span>히스토리를 선택하세요</span>
                    </EmptyMsg>
                ) : (
                    <Detail $visibleOnMobile={!isMobile || selectedSnap}>
                        <DetailTitle>{formatTime(details?.createdAt || selectedSnap.createdAt)}</DetailTitle>
                        <div style={{fontSize:'13px',color:'#4b5563',marginBottom:8}}>
                            Market Mood: <strong style={{color:details?.marketMood==='HOT'?'#ef4444':'#a78bfa'}}>
                                {details?.marketMood || selectedSnap.marketMood}
                            </strong>
                        </div>

                        {detailsLoading && <EmptyMsg><Loader size={20} color="#7c3aed"/>분석 로딩 중...</EmptyMsg>}

                        {details && !detailsLoading && (
                            <>
                                {/* V2.0 개선 정보 */}
                                <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}>
                                    {['ETF 필터 적용','Flat Base 8%','종가위치 60%+','52주 신고가'].map(tag=>(
                                        <span key={tag} style={{
                                            fontSize:'10px',padding:'2px 8px',borderRadius:4,
                                            background:'rgba(124,58,237,.1)',color:'#a78bfa',
                                            border:'1px solid rgba(124,58,237,.2)'
                                        }}>{tag}</span>
                                    ))}
                                </div>

                                {/* 전환점 */}
                                <SectionTitle>
                                    <Target size={14} color="#7c3aed"/>
                                    Identified Pivot Points ({details.pivotPoints?.length || 0})
                                </SectionTitle>
                                {(details.pivotPoints || []).map((item, i) => (
                                    <PivotCard key={item.code} style={{animationDelay:`${i*30}ms`}}>
                                        <div>
                                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                                                <span style={{fontWeight:700,color:'#e2e8f0'}}>{item.name}</span>
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{item.price?.toLocaleString()}원</span>
                                                <StatusBadge s={item.pivotStatus}>{item.pivotStatus}</StatusBadge>
                                            </div>
                                            <div style={{fontSize:'11px',color:'#374151'}}>
                                                Vol Spike: {item.volSurge?.toFixed(1)}x · Volatility: {item.volatility}
                                                {item.targetUpside && <span style={{marginLeft: 8, color: '#10b981', fontWeight: 600}}>🎯상승여력: {item.targetUpside}</span>}
                                                {item.closePosition && ` · 종가위치: ${item.closePosition}`}
                                                {item.highProximity && ` · 52W: ${item.highProximity}`}
                                            </div>
                                        </div>
                                        <div style={{display:'flex',alignItems:'center',gap:12}}>
                                            {item.performance && (
                                                <>
                                                     {[['TODAY', item.performance.todayMax],['NEXT DAY', item.performance.nextDayMax]].map(([label, val]) => {
                                                         const isNeutral = val === '-';
                                                         const isPositive = val.includes('+');
                                                         const color = isNeutral ? '#374151' : (isPositive ? '#ef4444' : '#3b82f6');
                                                         return (
                                                             <PerformanceBadge key={label} $neutral={isNeutral} style={{ 
                                                                 background: isNeutral ? 'rgba(255,255,255,.03)' : (isPositive ? 'rgba(239,68,68,.05)' : 'rgba(59, 130, 246,.05)'),
                                                                 borderColor: isNeutral ? '#1a1a2e' : (isPositive ? 'rgba(239,68,68,0.2)' : 'rgba(59, 130, 246,0.2)'),
                                                             }}>
                                                                 <span style={{fontSize:'9px',color:'#4b5563',textTransform:'uppercase'}}>{label} MAX</span>
                                                                 <span style={{fontSize:'13px',fontWeight:700,color}}>{val}</span>
                                                             </PerformanceBadge>
                                                         );
                                                     })}
                                                </>
                                            )}
                                            <ChevronRight size={16} color="#1a1a2e"/>
                                        </div>
                                    </PivotCard>
                                ))}
                            </>
                        )}
                    </Detail>
                )}
            </Layout>
        </Page>
    );
}
